import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Edit3 } from 'lucide-react';
import { ToolType, generateId } from './types';

const canAnnotateWithTool = (tool) => (
  tool === ToolType.PEN || tool === ToolType.MARKER || tool === ToolType.ERASER
);

const getPointerPos = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  return {
    x: (event.clientX - rect.left) / Math.max(1, rect.width),
    y: (event.clientY - rect.top) / Math.max(1, rect.height),
  };
};

const distanceToSegment = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
};

const pathHitByEraser = (path, eraserPoints, radius) => {
  const points = path.points || [];
  if (points.length < 2 || eraserPoints.length < 1) return false;
  for (let i = 1; i < points.length; i += 1) {
    for (const eraserPoint of eraserPoints) {
      if (distanceToSegment(eraserPoint, points[i - 1], points[i]) <= radius) {
        return true;
      }
    }
  }
  return false;
};

const drawAnnotationPath = (ctx, path, width, height) => {
  const points = path.points || [];
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x * width, points[0].y * height);
  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];
    ctx.lineTo(point.x * width, point.y * height);
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = path.width || 3;
  ctx.globalAlpha = path.tool === ToolType.MARKER ? 0.45 : 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.strokeStyle = path.tool === ToolType.ERASER ? '#0f172a' : (path.color || '#000');
  ctx.stroke();
  ctx.globalAlpha = 1;
};

const ElementAnnotationLayer = ({
  annotations,
  currentTool,
  currentColor,
  isAnnotating,
  onToggleAnnotating,
  onUpdate,
}) => {
  const canvasRef = useRef(null);
  const draftPathRef = useRef(null);
  const drawRafRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const safeAnnotations = useMemo(
    () => (Array.isArray(annotations) ? annotations : []),
    [annotations]
  );

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    [...safeAnnotations, draftPathRef.current].filter(Boolean).forEach((path) => {
      drawAnnotationPath(ctx, path, width, height);
    });
  }, [safeAnnotations]);

  const scheduleRedraw = useCallback(() => {
    if (drawRafRef.current != null) return;
    drawRafRef.current = window.requestAnimationFrame(() => {
      drawRafRef.current = null;
      redraw();
    });
  }, [redraw]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  useEffect(() => () => {
    if (drawRafRef.current != null) {
      window.cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = null;
    }
  }, []);

  const commitDraftPath = useCallback(() => {
    const draft = draftPathRef.current;
    draftPathRef.current = null;
    setIsDrawing(false);
    if (!draft || (draft.points || []).length < 2) {
      scheduleRedraw();
      return;
    }

    if (draft.tool === ToolType.ERASER) {
      const radius = Math.max(0.015, (draft.width || 20) / 800);
      onUpdate({
        annotations: safeAnnotations.filter((path) => !pathHitByEraser(path, draft.points || [], radius)),
      });
      return;
    }

    onUpdate({ annotations: [...safeAnnotations, draft] });
  }, [onUpdate, safeAnnotations, scheduleRedraw]);

  const handlePointerDown = useCallback((event) => {
    if (!isAnnotating || !canAnnotateWithTool(currentTool)) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    event.stopPropagation();
    canvas.setPointerCapture?.(event.pointerId);
    const point = getPointerPos(event, canvas);
    draftPathRef.current = {
      id: generateId(),
      tool: currentTool,
      color: currentColor,
      width: currentTool === ToolType.MARKER ? 14 : (currentTool === ToolType.ERASER ? 22 : 3),
      points: [point],
    };
    setIsDrawing(true);
  }, [currentColor, currentTool, isAnnotating]);

  const handlePointerMove = useCallback((event) => {
    if (!isAnnotating || !draftPathRef.current || !isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    event.stopPropagation();
    const events = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [event];
    const nextPoints = events.map((coalescedEvent) => getPointerPos(coalescedEvent, canvas));
    draftPathRef.current = {
      ...draftPathRef.current,
      points: [...(draftPathRef.current.points || []), ...nextPoints],
    };
    scheduleRedraw();
  }, [isAnnotating, isDrawing, scheduleRedraw]);

  const handlePointerUp = useCallback((event) => {
    if (!isAnnotating || !isDrawing) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget?.releasePointerCapture?.(event.pointerId);
    commitDraftPath();
  }, [commitDraftPath, isAnnotating, isDrawing]);

  return (
    <>
      <button
        type="button"
        className={`wb-element-annotate-btn ${isAnnotating ? 'active' : ''}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggleAnnotating();
        }}
        title={isAnnotating ? 'Done annotating' : 'Annotate'}
      >
        {isAnnotating ? <Check size={13} /> : <Edit3 size={13} />}
        <span>{isAnnotating ? 'Done' : 'Annotate'}</span>
      </button>
      <canvas
        ref={canvasRef}
        className={`wb-element-annotation-canvas ${isAnnotating ? 'annotating' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={(event) => event.stopPropagation()}
      />
    </>
  );
};

export default ElementAnnotationLayer;
